import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toggleFavoritePrompt } from '../api/data-connectors/toggle-favorite-prompt'
import { useNotification } from './useNotification'

export function useToggleFavoritePrompt() {
  const queryClient = useQueryClient()
  const { notify } = useNotification()
  const { t } = useTranslation()

  // Optimistic update pattern — "Via the UI" approach from TanStack Query docs:
  // https://tanstack.com/query/latest/docs/framework/react/guides/optimistic-updates#via-the-ui
  // The caller derives the displayed isFavorite state from isPending + variables
  // so the star flips instantly, while list reshuffling waits for the server (onSettled)
  return useMutation({
    mutationFn: ({ promptId }: { promptId: string }) => toggleFavoritePrompt(promptId),
    onSuccess: async () => {
      // Invalidate so the list reshuffles to reflect the new favorites order
      await queryClient.invalidateQueries({ queryKey: ['prompts'] })
    },
    onError: () => {
      // isPending going false automatically reverts displayedIsFavorite back to the prop value —
      // no cache restore needed; just notify the user
      notify({ isSuccess: false, text: t('prompts.favoriteFailureToast'), position: 'bottom' })
    },
  })
}
